import React, { useState, useEffect } from 'react';
import { useGameStore, TowerType, LevelType, DifficultyLevel } from '../store/gameStore';
import JokeViewer from './JokeViewer';
import { useResponsive } from '../hooks/useResponsive';

const GameUI: React.FC = () => {
  const { isMobile, isTablet, scaleFactor } = useResponsive();
  const {
    isPlaying,
    isPaused,
    wave,
    score,
    resources,
    lives,
    selectedTowerType,
    currentLevel,
    difficulty,
    startGame,
    pauseGame,
    resumeGame,
    resetGame,
    selectTowerType,
    nextWave,
    changeLevel,
    changeDifficulty,
    addTestJokeToDramaTowers,
  } = useGameStore();

  // State for joke viewer
  const [isJokeViewerOpen, setIsJokeViewerOpen] = useState(false);
  // State for intro guide
  const [showIntroGuide, setShowIntroGuide] = useState(false);
  // State for showing intro button
  const [showIntroButton, setShowIntroButton] = useState(false);
  // State for tracking if intro has been shown
  const [hasSeenIntro, setHasSeenIntro] = useState(false);

  // Show intro guide on component mount
  useEffect(() => {
    if (!hasSeenIntro) {
      setShowIntroGuide(true);
    }
  }, [hasSeenIntro]);

  // Tower options
  const towerOptions: { type: TowerType; name: string; description: string; cost: number }[] = [
    {
      type: 'science',
      name: 'Science',
      description: 'Long range',
      cost: 100,
    },
    {
      type: 'math',
      name: 'Math',
      description: 'High damage',
      cost: 150,
    },
    {
      type: 'art',
      name: 'Art',
      description: 'Area effect',
      cost: 125,
    },
    {
      type: 'drama',
      name: 'Drama',
      description: 'Freezes enemies',
      cost: 175,
    },
    {
      type: 'portal',
      name: 'Portal',
      description: 'Travel to other games',
      cost: 0,
    },
  ];

  // Level options
  const levelOptions: { type: LevelType; name: string; description: string }[] = [
    {
      type: 'default',
      name: 'Default School',
      description: 'Standard school layout',
    },
    {
      type: 'evanston',
      name: 'Evanston Township High',
      description: 'Based on real floor plan',
    },
  ];

  // Difficulty options
  const difficultyOptions: { type: DifficultyLevel; name: string; description: string }[] = [
    {
      type: 'easy',
      name: 'Easy',
      description: 'Towers +10%, Enemies -10%',
    },
    {
      type: 'normal',
      name: 'Normal',
      description: 'Standard balance',
    },
    {
      type: 'hard',
      name: 'Hard',
      description: 'Towers -10%, Enemies +10%',
    },
    {
      type: 'insane',
      name: 'Insane',
      description: 'Towers -20%, Enemies +20%',
    },
  ];

  // Styles for the UI components
  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1000,
  };

  const statsStyle: React.CSSProperties = {
    position: 'absolute',
    top: isMobile ? '10px' : '20px',
    left: isMobile ? '10px' : '20px',
    padding: isMobile ? '8px' : '15px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#ffffff',
    borderRadius: isMobile ? '5px' : '10px',
    fontFamily: 'Arial, sans-serif',
    display: 'flex',
    gap: isMobile ? '8px' : '15px',
    pointerEvents: 'auto',
    fontSize: isMobile ? '12px' : '14px',
  };

  const controlsStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: isMobile ? '10px' : '20px',
    left: isMobile ? '10px' : '20px',
    padding: isMobile ? '8px' : '15px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#ffffff',
    borderRadius: isMobile ? '5px' : '10px',
    fontFamily: 'Arial, sans-serif',
    display: 'flex',
    gap: isMobile ? '5px' : '10px',
    pointerEvents: 'auto',
    fontSize: isMobile ? '12px' : '14px',
  };

  const towerSelectionStyle: React.CSSProperties = {
    position: 'absolute',
    right: isMobile ? '5px' : '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: isMobile ? '5px' : '15px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#ffffff',
    borderRadius: isMobile ? '5px' : '10px',
    fontFamily: 'Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? '3px' : '10px',
    pointerEvents: 'auto',
  };

  const towerOptionStyle = (type: TowerType): React.CSSProperties => ({
    padding: isMobile ? '5px' : '10px',
    backgroundColor: selectedTowerType === type ? 'rgba(74, 74, 224, 0.5)' : 'rgba(0, 0, 0, 0.5)',
    color: '#ffffff',
    borderRadius: isMobile ? '3px' : '5px',
    cursor: 'pointer',
    border: `1px solid ${selectedTowerType === type ? 'white' : 'transparent'}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: isMobile ? '60px' : '100px',
    fontSize: isMobile ? '10px' : '12px',
  });

  const towerIconStyle = (type: TowerType): React.CSSProperties => ({
    width: isMobile ? '20px' : '30px',
    height: isMobile ? '20px' : '30px',
    borderRadius: '50%',
    backgroundColor: getTowerColor(type),
    marginBottom: isMobile ? '2px' : '5px',
  });

  const buttonStyle: React.CSSProperties = {
    padding: isMobile ? '5px 10px' : '8px 15px',
    backgroundColor: '#4a4ae0',
    color: '#ffffff',
    border: 'none',
    borderRadius: isMobile ? '3px' : '5px',
    cursor: 'pointer',
    fontSize: isMobile ? '12px' : '14px',
  };

  // Add debug button styles
  const debugButtonStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: isMobile ? '10px' : '20px',
    right: isMobile ? '10px' : '20px',
    padding: isMobile ? '5px 10px' : '8px 15px',
    backgroundColor: '#e04a9c',
    color: 'white',
    border: 'none',
    borderRadius: isMobile ? '3px' : '5px',
    cursor: 'pointer',
    pointerEvents: 'auto',
    zIndex: 100,
    fontSize: isMobile ? '12px' : '14px',
  };

  // Add joke library button styles
  const jokeLibraryButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: isMobile ? '10px' : '20px',
    right: isMobile ? '10px' : '20px',
    padding: isMobile ? '5px 10px' : '8px 15px',
    backgroundColor: '#9932cc',
    color: 'white',
    border: 'none',
    borderRadius: isMobile ? '3px' : '5px',
    cursor: 'pointer',
    pointerEvents: 'auto',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '3px' : '5px',
    fontSize: isMobile ? '12px' : '14px',
  };

  // Add intro guide modal styles
  const introGuideModalStyle: React.CSSProperties = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '800px',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    color: 'white',
    padding: '30px',
    borderRadius: '15px',
    zIndex: 3000,
    pointerEvents: 'auto',
    fontFamily: 'Arial, sans-serif',
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 2500,
    pointerEvents: 'auto',
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '15px',
    right: '15px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '5px',
  };

  const handleCloseIntroGuide = () => {
    setShowIntroGuide(false);
    setHasSeenIntro(true);
    setShowIntroButton(true);
  };

  return (
    <div style={containerStyle}>
      {/* Intro Guide Modal */}
      {showIntroGuide && (
        <>
          <div style={overlayStyle} onClick={handleCloseIntroGuide} />
          <div style={introGuideModalStyle}>
            <button style={closeButtonStyle} onClick={handleCloseIntroGuide}>×</button>
            <h2 style={{ color: '#9932cc', marginBottom: '20px', fontSize: '28px' }}>Welcome to School Tower Defense!</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#4a4ae0', marginBottom: '10px' }}>Game Overview</h3>
              <p>Defend your school from waves of troublemakers using educational towers. Place towers strategically to stop enemies from reaching their goal!</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#4a4ae0', marginBottom: '10px' }}>Tower Types</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {towerOptions.map(tower => (
                  <li key={tower.type} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <div style={{ 
                      width: '20px', 
                      height: '20px', 
                      borderRadius: '50%', 
                      backgroundColor: getTowerColor(tower.type),
                      marginRight: '10px' 
                    }} />
                    <div>
                      <strong>{tower.name}</strong> - {tower.description} (Cost: {tower.cost})
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#4a4ae0', marginBottom: '10px' }}>Controls</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '5px' }}>• WASD Keys: Move camera</li>
                <li style={{ marginBottom: '5px' }}>• Mouse Wheel: Zoom in/out</li>
                <li style={{ marginBottom: '5px' }}>• Right Mouse: Rotate camera</li>
                <li style={{ marginBottom: '5px' }}>• Left Click: Place selected tower</li>
              </ul>
            </div>

            <div>
              <h3 style={{ color: '#4a4ae0', marginBottom: '10px' }}>Tips</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '5px' }}>• Place towers strategically along enemy paths</li>
                <li style={{ marginBottom: '5px' }}>• Combine different tower types for maximum effect</li>
                <li style={{ marginBottom: '5px' }}>• Watch your resources - each tower costs money!</li>
                <li style={{ marginBottom: '5px' }}>• Start with easier difficulties to learn the game</li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Intro Guide Button */}
      {showIntroButton && hasSeenIntro && (
        <button 
          style={{
            position: 'absolute',
            bottom: '80px', // Position above the start game button
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '15px 30px',
            backgroundColor: '#ff4444',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            pointerEvents: 'auto',
            fontSize: '20px',
            fontWeight: 'bold',
            zIndex: 2000,
          }}
          onClick={() => setShowIntroGuide(true)}
        >
          Intro Guide
        </button>
      )}

      {/* Game stats */}
      <div style={statsStyle}>
        <div>Wave: {wave}</div>
        <div>Score: {score}</div>
        <div>Resources: {resources}</div>
        <div>Lives: {lives}</div>
        <div>Difficulty: {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}</div>
      </div>

      {/* Game controls */}
      <div style={controlsStyle}>
        {!isPlaying ? (
          <>
            <button style={buttonStyle} onClick={startGame}>Start Game</button>
            {/* Level selection */}
            <div>
              <span>Level: </span>
              <select 
                value={currentLevel} 
                onChange={(e) => changeLevel(e.target.value as LevelType)}
                disabled={isPlaying}
                style={{ marginRight: '10px' }}
              >
                {levelOptions.map((level) => (
                  <option key={level.type} value={level.type}>
                    {level.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Difficulty selection */}
            <div>
              <span>Difficulty: </span>
              <select 
                value={difficulty} 
                onChange={(e) => changeDifficulty(e.target.value as DifficultyLevel)}
                disabled={isPlaying}
              >
                {difficultyOptions.map((diffOption) => (
                  <option key={diffOption.type} value={diffOption.type}>
                    {diffOption.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : isPaused ? (
          <>
            <button style={buttonStyle} onClick={resumeGame}>Resume</button>
            <button style={buttonStyle} onClick={resetGame}>Restart</button>
          </>
        ) : (
          <>
            <button style={buttonStyle} onClick={pauseGame}>Pause</button>
            <button style={buttonStyle} onClick={() => nextWave()}>Next Wave</button>
          </>
        )}
      </div>

      {/* Tower selection */}
      <div style={towerSelectionStyle}>
        <h3 style={{ textAlign: 'center', margin: '0 0 10px 0', color: '#ffffff' }}>Towers</h3>
        {towerOptions.map((tower) => (
          <div
            key={tower.type}
            style={towerOptionStyle(tower.type)}
            onClick={() => selectTowerType(selectedTowerType === tower.type ? null : tower.type)}
          >
            <div style={towerIconStyle(tower.type)}></div>
            <p style={{ margin: '2px 0', textAlign: 'center', color: '#ffffff' }}>{tower.name}</p>
            <p style={{ margin: '2px 0', textAlign: 'center', color: '#ffffff' }}>{tower.cost}</p>
            <p style={{ margin: '2px 0', textAlign: 'center', fontSize: '0.8rem', color: '#ffffff' }}>{tower.description}</p>
          </div>
        ))}
      </div>

      {/* Joke Library Button */}
      <button 
        style={jokeLibraryButtonStyle} 
        onClick={() => setIsJokeViewerOpen(true)}
      >
        <span>📚</span> Joke Library
      </button>

      {/* Debug button for testing */}
      <button 
        style={debugButtonStyle} 
        onClick={() => addTestJokeToDramaTowers()}
      >
        Fix Joke Button
      </button>

      {/* Joke Viewer Component */}
      <JokeViewer 
        isOpen={isJokeViewerOpen} 
        onClose={() => setIsJokeViewerOpen(false)}
      />
    </div>
  );
};

// Helper function to get tower color
const getTowerColor = (type: TowerType): string => {
  switch (type) {
    case 'science':
      return '#4a4ae0';
    case 'math':
      return '#e04a4a';
    case 'art':
      return '#4ae04a';
    case 'drama':
      return '#9932cc'; // Purple color for drama tower
    case 'portal':
      return '#ffd700'; // Gold color for portal tower
    default:
      return '#4a4ae0';
  }
};

export default GameUI; 